import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Phone, MessageCircle, ExternalLink, AlertTriangle, Skull, Megaphone, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const fmtINR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

const ageBadgeClass = (days) => {
  if (days > 90) return 'bg-red-100 text-red-700 border-red-300';
  if (days > 30) return 'bg-orange-100 text-orange-700 border-orange-300';
  if (days > 14) return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-blue-100 text-blue-700 border-blue-300';
};

const RottenLeads = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [salesmanFilter, setSalesmanFilter] = useState('all');
  const [coachingId, setCoachingId] = useState(null); // lead_id currently being coached

  const load = async (sm) => {
    setLoading(true);
    try {
      const params = sm && sm !== 'all' ? { salesman_id: sm } : {};
      const res = await api.get('/admin/rotten-leads', { params });
      setData(res.data);
    } catch (e) {
      toast({
        title: 'Failed to load rotten leads',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCoach = async (lead) => {
    if (!window.confirm(`Send a coaching push to ${lead.salesman_name || 'salesman'} for this lead?`)) return;
    setCoachingId(lead.lead_id);
    try {
      const res = await api.post(`/admin/rotten-leads/${lead.lead_id}/coach`);
      toast({
        title: '✅ Coach nudge sent',
        description: res.data.push_sent > 0
          ? `${lead.salesman_name} will get a push notification.`
          : `Message logged. (No active push devices — salesman may need to re-enable notifications.)`,
      });
      // Refresh so coach_count updates
      await load(salesmanFilter);
    } catch (e) {
      toast({
        title: 'Failed to coach',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setCoachingId(null);
    }
  };

  useEffect(() => { load(salesmanFilter); }, [salesmanFilter]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 mt-4">Loading rotten leads…</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <p className="text-red-600">Unable to load.</p>
        <Button onClick={() => load(salesmanFilter)} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-emerald-700 p-2" data-testid="rotten-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate flex items-center gap-2">
              <Skull className="w-5 h-5" /> Rotten Leads
            </h1>
            <p className="text-xs text-emerald-100">Action needed today · {data.count} leads · {fmtINR(data.total_value)}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => load(salesmanFilter)} className="text-white hover:bg-emerald-700" data-testid="rotten-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-3">
        {/* Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Salesman:</label>
          <Select value={salesmanFilter} onValueChange={setSalesmanFilter}>
            <SelectTrigger className="flex-1" data-testid="rotten-salesman-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All salesmen</SelectItem>
              {data.salesmen.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary line */}
        {data.count === 0 ? (
          <Card className="border-emerald-300 bg-emerald-50">
            <CardContent className="pt-6 text-center">
              <p className="text-2xl mb-2">🎉</p>
              <p className="text-emerald-700 font-semibold">No rotten leads!</p>
              <p className="text-xs text-emerald-600 mt-1">All active leads are healthy and being worked.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-xs text-gray-500 px-1">
              Sorted by value × age. Focus on the top 5 first.
            </p>

            {/* Lead cards */}
            {data.leads.map((l, idx) => (
              <Card key={l.lead_id} className={`border-l-4 ${l.age_days > 90 ? 'border-l-red-500' : l.age_days > 30 ? 'border-l-orange-500' : 'border-l-amber-500'}`} data-testid={`rotten-lead-${idx}`}>
                <CardContent className="pt-4 pb-4">
                  {/* Top row: rank + client */}
                  <div className="flex items-start gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{l.client_name || 'Unnamed'}</p>
                      <p className="text-xs text-gray-500 truncate">{l.client_phone || '—'}</p>
                    </div>
                    <Badge className={`${ageBadgeClass(l.age_days)} border text-xs`}>
                      {l.age_days}d old
                    </Badge>
                  </div>

                  {/* Product + value */}
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">{l.product_name}</p>
                      {l.main_group && <p className="text-[11px] text-gray-500">{l.main_group}</p>}
                    </div>
                    <p className="text-sm font-bold text-gray-900 ml-2">{fmtINR(l.estimated_value)}</p>
                  </div>

                  {/* Status + salesman */}
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    <Badge variant={l.status === 'parked' ? 'secondary' : 'outline'}>
                      {l.status.toUpperCase()}
                    </Badge>
                    <span className="text-gray-500 truncate">👤 {l.salesman_name || '—'}</span>
                    {l.follow_up_date && (
                      <span className="text-gray-500 whitespace-nowrap">📅 {l.follow_up_date}</span>
                    )}
                  </div>

                  {/* Rot reasons */}
                  <div className="mb-3 space-y-1">
                    {l.rot_reasons.map((r, i) => (
                      <div key={i} className="text-xs text-red-700 bg-red-50 rounded px-2 py-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {r}
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 min-w-[110px]"
                      onClick={() => navigate(`/lead-sessions/${l.session_id}?tab=leads`)}
                      data-testid={`open-session-${idx}`}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> Open
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 min-w-[110px] bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={() => handleCoach(l)}
                      disabled={coachingId === l.lead_id}
                      data-testid={`coach-${idx}`}
                    >
                      {coachingId === l.lead_id ? (
                        <>Sending…</>
                      ) : l.coach_count > 0 ? (
                        <><Check className="w-3 h-3 mr-1" /> Coach again{l.coach_count > 1 ? ` (${l.coach_count})` : ''}</>
                      ) : (
                        <><Megaphone className="w-3 h-3 mr-1" /> Coach</>
                      )}
                    </Button>
                    {l.client_phone && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`tel:${l.client_phone}`, '_self')}
                          data-testid={`call-${idx}`}
                        >
                          <Phone className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`https://wa.me/${l.client_phone.replace(/[^0-9]/g, '')}`, '_blank')}
                          data-testid={`whatsapp-${idx}`}
                        >
                          <MessageCircle className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                  {l.coach_last_sent_at && (
                    <p className="text-[10px] text-gray-400 mt-2">
                      Last coached: {new Date(l.coach_last_sent_at).toLocaleString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </>
        )}

        <p className="text-center text-[11px] text-gray-400 pt-4">
          Report generated at {new Date(data.generated_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
};

export default RottenLeads;
